import type { ReactNode, ThHTMLAttributes } from 'react';

/**
 * Conservative table chrome primitive. Pages still own row rendering;
 * this normalises the surrounding wrapper, header/body styling, empty state,
 * <caption>/scope semantics, and aria-sort. Compose with <SortableHeader/>
 * for sortable columns.
 */

interface DataTableProps {
  caption?: string;
  visuallyHideCaption?: boolean;
  emptyState?: ReactNode;
  isEmpty?: boolean;
  className?: string;
  containerClassName?: string;
  children: ReactNode;
}

export default function DataTable({
  caption,
  visuallyHideCaption = true,
  emptyState,
  isEmpty = false,
  className = '',
  containerClassName = '',
  children,
}: DataTableProps) {
  if (isEmpty && emptyState) {
    return <div className={`p-6 text-center text-gray-600 dark:text-gray-300 ${containerClassName}`.trim()}>{emptyState}</div>;
  }
  return (
    <div className={`overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0 ${containerClassName}`.trim()}>
      <table className={`min-w-full divide-y divide-gray-200 dark:divide-gray-700 ${className}`.trim()}>
        {caption ? (
          <caption className={visuallyHideCaption ? 'sr-only' : 'caption-bottom text-sm text-gray-600 dark:text-gray-300 py-2'}>
            {caption}
          </caption>
        ) : null}
        {children}
      </table>
    </div>
  );
}

interface DataTableHeadProps {
  children: ReactNode;
  className?: string;
}

function DataTableHead({ children, className = '' }: DataTableHeadProps) {
  return (
    <thead className={`bg-gray-50 dark:bg-gray-800 ${className}`.trim()}>
      <tr>{children}</tr>
    </thead>
  );
}

interface DataTableHeaderCellProps extends ThHTMLAttributes<HTMLTableCellElement> {
  /**
   * The current sort direction for this column when it's the active one.
   * Pass `false` (or omit) on non-sortable columns; pass `'asc' | 'desc' | 'none'` on sortable ones.
   */
  sortDirection?: 'asc' | 'desc' | 'none' | false;
  children: ReactNode;
}

function DataTableHeaderCell({
  sortDirection = false,
  scope = 'col',
  className = '',
  children,
  ...rest
}: DataTableHeaderCellProps) {
  const ariaSort: 'ascending' | 'descending' | 'none' | undefined =
    sortDirection === 'asc' ? 'ascending' : sortDirection === 'desc' ? 'descending' : sortDirection === 'none' ? 'none' : undefined;
  return (
    <th
      scope={scope}
      aria-sort={ariaSort}
      className={`px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-700 dark:text-gray-200 ${className}`.trim()}
      {...rest}
    >
      {children}
    </th>
  );
}

interface DataTableBodyProps {
  children: ReactNode;
  className?: string;
}

function DataTableBody({ children, className = '' }: DataTableBodyProps) {
  return (
    <tbody className={`divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-900 ${className}`.trim()}>
      {children}
    </tbody>
  );
}

interface DataTableRowProps {
  children: ReactNode;
  className?: string;
  onClick?: () => void;
  /** When true, renders the row with a clickable visual affordance. */
  interactive?: boolean;
}

function DataTableRow({ children, className = '', onClick, interactive = false }: DataTableRowProps) {
  const base = 'hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors';
  const click = interactive || onClick ? 'cursor-pointer focus-within:bg-gray-50 dark:focus-within:bg-gray-800' : '';
  return (
    <tr className={`${base} ${click} ${className}`.trim()} onClick={onClick}>
      {children}
    </tr>
  );
}

interface DataTableCellProps {
  children: ReactNode;
  className?: string;
  scope?: 'row';
  colSpan?: number;
}

function DataTableCell({ children, className = '', scope, colSpan }: DataTableCellProps) {
  if (scope === 'row') {
    return (
      <th scope="row" className={`px-3 py-3 text-sm font-medium text-gray-900 dark:text-gray-100 ${className}`.trim()} colSpan={colSpan}>
        {children}
      </th>
    );
  }
  return (
    <td className={`px-3 py-3 text-sm text-gray-700 dark:text-gray-200 ${className}`.trim()} colSpan={colSpan}>
      {children}
    </td>
  );
}

DataTable.Head = DataTableHead;
DataTable.HeaderCell = DataTableHeaderCell;
DataTable.Body = DataTableBody;
DataTable.Row = DataTableRow;
DataTable.Cell = DataTableCell;
